import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [empresa, setEmpresa] = useState(null);

    // Configure axios defaults
    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common['Authorization'];
        }
    }, [token]);

    // Fetch user profile on mount if token exists
    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await axios.get(`${API_URL}/api/auth/me`);
            setUser(response.data);
            
            // Fetch empresa if user has one
            if (response.data.empresa_id) {
                try {
                    const empresaRes = await axios.get(`${API_URL}/api/empresas/${response.data.empresa_id}`);
                    setEmpresa(empresaRes.data);
                } catch (e) {
                    console.error('Error fetching empresa:', e);
                }
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        const response = await axios.post(`${API_URL}/api/auth/login`, {
            email,
            password
        });
        
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        
        // Fetch empresa if user has one
        if (userData.empresa_id) {
            try {
                const empresaRes = await axios.get(`${API_URL}/api/empresas/${userData.empresa_id}`);
                setEmpresa(empresaRes.data);
            } catch (e) {
                console.error('Error fetching empresa:', e);
            }
        }
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setEmpresa(null);
        delete axios.defaults.headers.common['Authorization'];
    };

    const isSuperAdmin = () => user?.rol === 'superadmin';
    const isAdminEmpresa = () => user?.rol === 'admin_empresa';
    const isFinanzas = () => user?.rol === 'finanzas';
    const isEjecutivo = () => user?.rol === 'ejecutivo';
    const isSoloLectura = () => user?.rol === 'solo_lectura';

    const canEdit = () => ['superadmin', 'admin_empresa', 'finanzas', 'ejecutivo'].includes(user?.rol);
    const canDelete = () => ['superadmin', 'admin_empresa'].includes(user?.rol);
    const canManageUsers = () => ['superadmin', 'admin_empresa'].includes(user?.rol);

    const value = {
        user,
        token,
        empresa,
        loading,
        login,
        logout,
        isSuperAdmin,
        isAdminEmpresa,
        isFinanzas,
        isEjecutivo,
        isSoloLectura,
        canEdit,
        canDelete,
        canManageUsers,
        refreshUser: fetchUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
